import React, { useRef, useState } from "react";
import { Play, Subtitles } from "lucide-react";
import { company } from "../mock";
import { visitorLang } from "../lib/api";

import { tr, L, LANGS } from "../i18n";
const BASE = "https://customer-assets-rejwkqb3.emergentagent.net/job_cash-app-rebuild/artifacts/";
const VIDEOS = L([
  { id: "account", title: "Opening your company account", url: `${BASE}e29a49624aeec3eb_Open%20Your%20PAYG8%20Account%20v1.mp4` },
  { id: "personal", title: "Opening your personal account", url: `${BASE}31f121957b253954_Open%20Your%20Personal%20PAYG8%20Account%20v1.mp4` },
]);

const Poster = ({ title }) => (
  <div className="absolute inset-0 bg-[#0b1f3a] overflow-hidden">
    <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full border border-[#c2a15a]/20" />
    <div className="absolute -bottom-20 -left-10 w-72 h-72 rounded-full border border-[#c2a15a]/10" />
    <div className="absolute left-6 top-6 flex items-center gap-2">
      <span className="font-display text-[26px] leading-none text-white">{company.brandMark}</span>
      <span className="flex flex-col leading-none">
        <span className="text-[9px] tracking-[0.35em] text-[#c2a15a] font-medium">{company.brandSub}</span>
        <span className="text-[8px] tracking-[0.2em] text-white/50">{tr("LAWYERS LLP")}</span>
      </span>
    </div>
    <p className="absolute left-6 bottom-6 right-6 font-display text-white text-[20px] md:text-[24px] leading-tight">{title}</p>
  </div>
);

function VideoPlayer({ id, title, url }) {
  const lang = visitorLang();
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState(false);

  const handlePlay = () => {
    const p = videoRef.current?.play();
    if (p && typeof p.then === "function") p.then(() => setPlaying(true)).catch(() => setError(true));
    else setPlaying(true);
  };

  return (
    <figure className="reveal" data-testid={`video-${id}`}>
      <div className="relative rounded-2xl overflow-hidden ring-1 ring-[#c2a15a]/30 shadow-2xl shadow-black/40 bg-black">
        <video ref={videoRef} src={url} className="w-full h-auto block aspect-video" controls={playing}
          playsInline preload="metadata" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
          onError={() => setError(true)} crossOrigin="anonymous">
          {LANGS.map((l) => (
            <track key={l.code} kind="subtitles" src={`/subtitles/${id}.${l.code}.vtt`} srcLang={l.code} label={l.label} default={l.code === lang} />
          ))}
        </video>
        {!playing && !error && (
          <button onClick={handlePlay} aria-label={tr("Play video: {v0}", { v0: title })} data-testid={`video-${id}-play`}
            className="absolute inset-0 flex items-center justify-center group">
            <Poster title={title} />
            <span className="relative w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#c2a15a] group-hover:bg-[#d8bd82] flex items-center justify-center shadow-xl transition-transform group-hover:scale-105">
              <Play size={28} className="text-[#0b1f3a] ml-1" fill="currentColor" />
            </span>
          </button>
        )}
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <Poster title={title} />
            <p className="relative text-white/80 text-[14px] mb-4">{tr("The video can't be played in this browser.")}</p>
            <a href={url} target="_blank" rel="noopener noreferrer"
              className="relative inline-flex items-center gap-2 bg-[#c2a15a] hover:bg-[#d8bd82] text-[#0b1f3a] font-semibold uppercase tracking-wide text-[12px] px-5 py-2.5 rounded-full transition-colors">
              {tr("Open video in a new tab")}
            </a>
          </div>
        )}
      </div>
      <figcaption className="flex items-center gap-2 text-white/55 text-[13px] mt-4">
        <Subtitles size={15} className="text-[#c2a15a]" /> {tr("Subtitles in English, German, Spanish, French and Italian – select via the CC button in the player")}
      </figcaption>
    </figure>
  );
}

export default function VideoSection() {
  return (
    <section id="video" className="bg-[#0b1f3a] py-20 md:py-28 relative overflow-hidden scroll-mt-20">
      <div className="pointer-events-none absolute -top-20 -left-20 w-[380px] h-[380px] rounded-full border border-[#c2a15a]/10" />
      <div className="pointer-events-none absolute -bottom-24 -right-16 w-[320px] h-[320px] rounded-full border border-[#c2a15a]/10" />

      <div className="max-w-6xl mx-auto px-5 md:px-8 relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-10 reveal">
          <div className="deco-line w-24 mx-auto mb-6" />
          <p className="text-[#c2a15a] text-[13px] tracking-[0.25em] uppercase mb-3">{tr("Watch")}</p>
          <h2 className="font-display text-[34px] md:text-[46px] text-white leading-tight">{tr("How to open your account")}</h2>
          <p className="text-white/60 text-[17px] mt-4">{tr("Short walkthroughs of getting started with R&H Savings.")}</p>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          {VIDEOS.map((v) => <VideoPlayer key={v.id} {...v} />)}
        </div>
      </div>
    </section>
  );
}
